import React, { useCallback, useEffect, useRef, useState } from 'react';
import tw from 'twin.macro';

import { PreviewPlan } from 'api/video';
import {
  changeoverShown,
  cropStyle,
  driftStyle,
  photoAt,
  photoPlacement,
  PhotoSpot,
  picturesToPrefetch,
  placeLayer,
} from './previewTimeline';

/**
 * SYM-6206. One frame of a Track 1 preview: the photo, and the pictures on top.
 *
 * ⚠️ NOTHING HERE DECIDES HOW THE VIDEO LOOKS. Every picture was drawn by the
 * server with the code that composites the video, so the text is already broken
 * into lines, already the right size, already the right colour, and already in
 * the typeface of the chosen style. This places them and nothing more.
 *
 * Positions are percentages of the video's own canvas rather than pixels, so the
 * pane can be any size and the frame still matches the render exactly.
 */

const Stage = tw.div`absolute inset-0 overflow-hidden bg-black`;
const PhotoLayer = tw.div`absolute inset-0 overflow-hidden`;
/**
 * SYM-6221. The frame-sized box the drift scales. It is a box of its own, inside
 * the clipping layer, so growing it never grows the clip and the photo simply
 * runs past the edges, as it does in the video.
 */
const Cover = tw.div`absolute inset-0`;

interface Track1PreviewFrameProps {
  plan: PreviewPlan;
  currentTime: number;
  /** Drives the music: the video has a track, so a silent preview is not it. */
  isPlaying?: boolean;
}

/** How far the music may drift from the clock before it is pulled back. */
const AUDIO_DRIFT_SECONDS = 0.25;

/**
 * SYM-6298, 22 September 2026. The handlers that say "this picture is here".
 *
 * Raised in review: readiness used to be noticed in ONE place, the see-through
 * copies fetched ahead of time, and a picture could be perfectly available
 * through the photo that was showing it while those copies had not got round to
 * it. The changeover then held a picture off the screen that was in the
 * browser's hands already. So every <img> in this file reports, whichever job
 * it is doing, and the same picture reporting twice costs nothing.
 *
 * The `ref` is not the same thing as `onLoad`: a picture already in the
 * browser's store is finished before React has attached a handler, and then
 * `load` never fires here at all. `complete` is the browser's own answer to
 * "is this fetch over", and a width of nothing means it ended badly.
 */
/**
 * SYM-6298, 22 September 2026. How badly the browser should want this picture.
 *
 * With the fetching no longer done one at a time, the only thing standing
 * between four requests and a slow connection is telling the browser which one
 * matters. `fetchpriority` is its own word for that, and it is handed over as a
 * plain attribute because the React typings this project carries are older than
 * the attribute is.
 *
 * Hypothesis, unverified: a browser that has never heard of it ignores it and
 * falls back to the order the pictures are written in, which is why that order
 * is deliberate. Nothing here was measured against a throttled connection.
 */
const urgency = (priority: 'high' | 'low'): Record<string, string> => ({
  fetchpriority: priority,
});

const reportWhenHere = (url: string, note: (url: string) => void) => ({
  onLoad: () => note(url),
  ref: (element: HTMLImageElement | null) => {
    if (element?.complete && element.naturalWidth > 0) note(url);
  },
});

/**
 * A photo, cropped the way the customer cropped it, covering the frame -- and on
 * the Dynamic look, drifting (SYM-6221): the cover-fitted photo grows slowly
 * about the point the server aimed at, on the slide's own clock.
 *
 * SYM-6298. `spot` carries the changeover: the whole frame-sized layer is shifted
 * by a percentage of its own size, which is the frame's size, so a shift of 100
 * puts it exactly one frame away. The shift sits on the OUTER layer and the
 * drift's scale stays on the box inside it, so the two compose instead of one
 * overwriting the other -- and the crop's percentage sizing, which is on the
 * picture inside that, is not touched at all. The layer clips to itself while it
 * travels, so the two photos tile the frame rather than overlap.
 */
const Photo: React.FC<{
  plan: PreviewPlan;
  index: number;
  spot: PhotoSpot;
  currentTime: number;
  /** SYM-6298. This picture is on screen AND here: say so, like any other. */
  onArrived: (url: string) => void;
}> = ({ plan, index, spot, currentTime, onArrived }) => {
  const slide = plan.slides[index];

  if (!slide?.url) return null;

  return (
    <PhotoLayer
      // The pictures being fetched ahead of time are <img> elements in this same
      // stage, so "the photos" cannot be found by looking for pictures. This
      // names the two that are actually on screen. SYM-6298.
      data-testid="preview-photo"
      style={{
        opacity: spot.opacity,
        transform: `translate(${spot.x}%, ${spot.y}%)`,
      }}
    >
      <Cover style={driftStyle(slide, currentTime)}>
        <img
          src={slide.url}
          alt=""
          draggable={false}
          {...reportWhenHere(slide.url, onArrived)}
          style={{
            position: 'absolute',
            objectFit: 'cover',
            ...cropStyle(slide),
          }}
        />
      </Cover>
    </PhotoLayer>
  );
};

export const Track1PreviewFrame: React.FC<Track1PreviewFrameProps> = ({
  plan,
  currentTime,
  isPlaying = false,
}) => {
  const audio = useRef<HTMLAudioElement>(null);

  /**
   * SYM-6298, 21 September 2026. Which of the plan's pictures are actually here.
   *
   * The changeover reads this and refuses to move until the picture it is
   * bringing in has arrived, because a photo that slides aside too early leaves
   * bare stage behind it. It is never emptied: a picture that has once arrived
   * is in the browser's own store, and clearing this on a new plan would make
   * an already-cached picture count as missing and hold a changeover for
   * nothing. A handful of addresses is not worth housekeeping.
   */
  const [arrived, setArrived] = useState<ReadonlySet<string>>(new Set());

  // Adding an address already known changes nothing and hands back the very
  // same set, so React stops there rather than drawing the frame again. That
  // is what makes it safe for every picture in the stage to report freely.
  const noteArrived = useCallback((url: string) => {
    setArrived((known) => (known.has(url) ? known : new Set(known).add(url)));
  }, []);

  const prefetching = picturesToPrefetch(plan.slides);

  // Follow the clock rather than run beside it: the clock is what the pictures
  // are drawn from, so anything else would drift away from what is on screen.
  useEffect(() => {
    const track = audio.current;
    if (!track) return;

    if (isPlaying) {
      // Only ever reached from the play button, so no autoplay policy applies.
      void track.play().catch(() => undefined);
    } else {
      track.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const track = audio.current;
    if (!track) return;

    if (Math.abs(track.currentTime - currentTime) > AUDIO_DRIFT_SECONDS) {
      track.currentTime = currentTime;
    }
  }, [currentTime]);

  const photo = photoAt(plan.slides, currentTime);
  const percentX = (value: number) => `${(value / plan.width) * 100}%`;
  const percentY = (value: number) => `${(value / plan.height) * 100}%`;

  // SYM-6298. The changeover's own name decides the movement -- and whether it
  // may move at all yet, which it may not until the picture it brings in has
  // arrived. The name belongs to the slide being crossed INTO, which is how the
  // server hangs it (`transition` on the arriving slide).
  const shown = changeoverShown(plan.slides, photo, arrived);
  const spots = photoPlacement(shown.name, shown.mix);

  return (
    <Stage>
      <Photo
        plan={plan}
        index={photo.from}
        spot={spots.from}
        currentTime={currentTime}
        onArrived={noteArrived}
      />
      {photo.to !== null && (
        <Photo
          plan={plan}
          index={photo.to}
          spot={spots.to}
          currentTime={currentTime}
          onArrived={noteArrived}
        />
      )}

      {plan.layers.map((layer) => {
        const placed = placeLayer(layer, currentTime);

        if (!placed.visible) return null;

        return (
          <img
            key={layer.name}
            src={layer.url}
            alt=""
            draggable={false}
            // The global reset caps every <img> at `max-width: 100%` (see
            // cropStyle, SYM-6272). Minimal's soft backing is WIDER than the
            // frame on purpose, so it can fade out past the edges: 1784 px on
            // a 1080 frame for "Attractiepark sfeer". Under the cap it was
            // squeezed to the frame's width with its left edge left where it
            // was, which put the patch and the letter shadow off to the left
            // of the words (QA, 30 Sep 2026). The video draws it full size.
            style={{
              position: 'absolute',
              left: percentX(placed.x),
              top: percentY(placed.y),
              width: percentX(layer.width),
              height: percentY(layer.height),
              maxWidth: 'none',
              maxHeight: 'none',
            }}
          />
        );
      })}

      {plan.audio_url && (
        <audio ref={audio} src={plan.audio_url} preload="auto" />
      )}

      {/*
        SYM-6298. Every slide's picture, fetched ahead of its own changeover.
        These are the same addresses the photos above use, so the browser asks
        for each one once and the photo is already in hand when its moment
        comes. They are a pixel across, see-through and deaf to the pointer:
        present so the fetch happens and so its finishing can be noticed, and
        invisible in every other respect.

        They come AFTER the photos on purpose. A browser works through a page's
        pictures roughly in the order it meets them, so the one actually on
        screen is asked for first simply by being written first, and the urgency
        below says the same thing again in the way a browser is meant to be told.
      */}
      {prefetching.map(({ url, priority }) => (
        <img
          key={url}
          src={url}
          alt=""
          aria-hidden
          draggable={false}
          data-testid="preview-prefetch"
          data-priority={priority}
          {...urgency(priority)}
          {...reportWhenHere(url, noteArrived)}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      ))}
    </Stage>
  );
};

export default Track1PreviewFrame;
